"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import Link from "next/link";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { type CertifiedMedicine } from "@/entities/certified-medicine";
import { getShelfLifeStatus, type ShelfLifeStatus } from "@/shared/lib/expiry-status";
import { formatDayMonthYear } from "@/shared/lib/format/dates";
import { toIntlLocale } from "@/shared/lib/format/locale";
import { cn } from "@/shared/lib/utils";
import { Badge } from "@/shared/ui/badge";
import { FormattedDate } from "@/shared/ui/formatted-date";
const SOURCE_BASE_URL = "https://www.uzpharm-control.uz/ru/registries/certified-medicines/view";
const SHELF_LIFE_STATUS_DOT: Record<ShelfLifeStatus, string> = {
  active: "bg-emerald-500",
  expiringSoon: "bg-amber-500",
  expired: "bg-red-500",
  unknown: "bg-muted-foreground/30"
};
type MedicineCertificatesListProps = {
  items: CertifiedMedicine[];
  loading?: boolean;
  error?: string | null;
};
type OrganizationGroup = {
  key: string;
  label: string;
  count: number;
};
const ALL_GROUP_KEY = "__all__";
const UNKNOWN_ORGANIZATION_KEY = "__unknown_organization__";
function compareByIssuedDateDesc(a: CertifiedMedicine, b: CertifiedMedicine) {
  const aTime = a.certificateIssuedAt ? Date.parse(a.certificateIssuedAt) : Number.NaN;
  const bTime = b.certificateIssuedAt ? Date.parse(b.certificateIssuedAt) : Number.NaN;
  const aValid = Number.isFinite(aTime);
  const bValid = Number.isFinite(bTime);
  if (aValid && bValid && aTime !== bTime) return bTime - aTime;
  if (aValid && !bValid) return -1;
  if (!aValid && bValid) return 1;
  return b.id - a.id;
}
function normalizeOrganizationText(value: string | null | undefined) {
  return value?.trim().replace(/\s+/g, " ") ?? "";
}
function getOrganizationGroupInfo(item: CertifiedMedicine, unknownLabel: string) {
  const linkedOrganization = item.orderedByOrganization;
  if (linkedOrganization) {
    return {
      key: `organization:${linkedOrganization.id}`,
      label: normalizeOrganizationText(linkedOrganization.name) || unknownLabel
    };
  }
  const orderedBy = normalizeOrganizationText(item.orderedBy);
  if (orderedBy) {
    return {
      key: `ordered-by:${orderedBy.toLocaleLowerCase()}`,
      label: orderedBy
    };
  }
  return {
    key: UNKNOWN_ORGANIZATION_KEY,
    label: unknownLabel
  };
}
function buildOrganizationGroups(items: CertifiedMedicine[], unknownLabel: string): OrganizationGroup[] {
  const groups = new Map<string, OrganizationGroup>();
  for (const item of items) {
    const groupInfo = getOrganizationGroupInfo(item, unknownLabel);
    const existing = groups.get(groupInfo.key);
    if (existing) {
      existing.count += 1;
    } else {
      groups.set(groupInfo.key, {
        ...groupInfo,
        count: 1
      });
    }
  }
  return [...groups.values()].sort((a, b) => {
    if (a.count !== b.count) return b.count - a.count;
    return a.label.localeCompare(b.label);
  });
}
export function MedicineCertificatesList(t0) {
  const $ = _c(49);
  const {
    items,
    loading,
    error
  } = t0;
  const {
    t,
    i18n
  } = useTranslation();
  const intlLocale = toIntlLocale(i18n.language);
  let t1;
  if ($[0] !== items) {
    t1 = [...items].sort(compareByIssuedDateDesc);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sortedItems = t1;
  let t2;
  if ($[2] !== t) {
    t2 = t("certifiedMedicines.filters.unknownOrganization", {
      defaultValue: "Unknown organization"
    });
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const unknownOrganizationLabel = t2;
  let t3;
  if ($[4] !== sortedItems || $[5] !== unknownOrganizationLabel) {
    t3 = buildOrganizationGroups(sortedItems, unknownOrganizationLabel);
    $[4] = sortedItems;
    $[5] = unknownOrganizationLabel;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const organizationGroups = t3;
  const [activeGroupKey, setActiveGroupKey] = React.useState(ALL_GROUP_KEY);
  let t4;
  let t5;
  if ($[7] !== activeGroupKey || $[8] !== organizationGroups) {
    t4 = () => {
      if (activeGroupKey === ALL_GROUP_KEY) {
        return;
      }
      if (!organizationGroups.some(group => group.key === activeGroupKey)) {
        setActiveGroupKey(ALL_GROUP_KEY);
      }
    };
    t5 = [activeGroupKey, organizationGroups];
    $[7] = activeGroupKey;
    $[8] = organizationGroups;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  React.useEffect(t4, t5);
  let t6;
  bb0: {
    if (activeGroupKey === ALL_GROUP_KEY) {
      t6 = sortedItems;
      break bb0;
    }
    let t7;
    if ($[11] !== activeGroupKey || $[12] !== sortedItems || $[13] !== unknownOrganizationLabel) {
      let t8;
      if ($[15] !== activeGroupKey || $[16] !== unknownOrganizationLabel) {
        t8 = item => getOrganizationGroupInfo(item, unknownOrganizationLabel).key === activeGroupKey;
        $[15] = activeGroupKey;
        $[16] = unknownOrganizationLabel;
        $[17] = t8;
      } else {
        t8 = $[17];
      }
      t7 = sortedItems.filter(t8);
      $[11] = activeGroupKey;
      $[12] = sortedItems;
      $[13] = unknownOrganizationLabel;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    t6 = t7;
  }
  const visibleItems = t6;
  if (error) {
    let t7;
    if ($[18] !== error) {
      t7 = <p className="text-destructive text-sm">{error}</p>;
      $[18] = error;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    return t7;
  }
  if (loading && sortedItems.length === 0) {
    let t7;
    if ($[20] !== t) {
      t7 = t("certifiedMedicines.title");
      $[20] = t;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    let t8;
    if ($[22] !== t7) {
      t8 = <p className="text-muted-foreground text-sm">{t7}…</p>;
      $[22] = t7;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    return t8;
  }
  if (sortedItems.length === 0) {
    let t7;
    if ($[24] !== t) {
      t7 = t("certifiedMedicines.emptyTitle");
      $[24] = t;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    let t8;
    if ($[26] !== t7) {
      t8 = <p className="text-muted-foreground text-sm">{t7}</p>;
      $[26] = t7;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    return t8;
  }
  const t7 = "flex flex-col gap-3";
  let t8;
  if ($[28] !== t) {
    t8 = t("certifiedMedicines.columns.orderedBy");
    $[28] = t;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const t9 = activeGroupKey === ALL_GROUP_KEY;
  let t10;
  if ($[30] !== t) {
    t10 = t("common.filters.all", {
      defaultValue: "All"
    });
    $[30] = t;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setActiveGroupKey(ALL_GROUP_KEY);
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== sortedItems.length || $[34] !== t10 || $[35] !== t9) {
    t12 = <OrganizationCounterButton active={t9} label={t10} count={sortedItems.length} onClick={t11} />;
    $[33] = sortedItems.length;
    $[34] = t10;
    $[35] = t9;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== activeGroupKey || $[38] !== organizationGroups) {
    let t14;
    if ($[40] !== activeGroupKey) {
      t14 = group_0 => <OrganizationCounterButton key={group_0.key} active={activeGroupKey === group_0.key} label={group_0.label} count={group_0.count} onClick={() => setActiveGroupKey(group_0.key)} />;
      $[40] = activeGroupKey;
      $[41] = t14;
    } else {
      t14 = $[41];
    }
    t13 = organizationGroups.map(t14);
    $[37] = activeGroupKey;
    $[38] = organizationGroups;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[42] !== t12 || $[43] !== t13 || $[44] !== t8) {
    t14 = <div className="scrollbar-hidden -mx-1 flex gap-1 overflow-x-auto px-1 pb-0.5" aria-label={t8}>{t12}{t13}</div>;
    $[42] = t12;
    $[43] = t13;
    $[44] = t8;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  const t15 = visibleItems.length === 0 ? <p className="text-muted-foreground text-sm">{t("certifiedMedicines.emptyTitle")}</p> : <ul className="flex flex-col gap-2">{visibleItems.map(item_0 => {
      const status = getShelfLifeStatus(item_0.shelfLifeDate);
      const statusLabel = status === "unknown" ? undefined : t(`expiryStatus.${status}` as const);
      const issued = formatDayMonthYear(item_0.certificateIssuedAt, intlLocale);
      const shelfLife = formatDayMonthYear(item_0.shelfLifeDate, intlLocale) || item_0.shelfLifeRaw;
      const orderedByOrg = item_0.orderedByOrganization;
      const orderedByLabel = orderedByOrg?.name ?? item_0.orderedBy;
      const sourceLabel = t("certifiedMedicines.detail.openSource");
      return <li key={item_0.id}><div className="border-border/60 hover:bg-muted/40 group relative flex flex-col gap-1.5 rounded-lg border px-3 py-2 transition-colors"><a href={`${SOURCE_BASE_URL}/${item_0.id}`} target="_blank" rel="noopener noreferrer" aria-label={sourceLabel} className="focus-visible:ring-ring/40 absolute inset-0 z-0 rounded-lg focus-visible:ring-2 focus-visible:outline-none" /><div className="pointer-events-none flex items-start justify-between gap-2"><span className="text-foreground text-sm font-medium">{item_0.productName}</span><ExternalLink className="text-muted-foreground group-hover:text-foreground mt-0.5 size-3.5 shrink-0 transition-colors" aria-hidden="true" /></div>{item_0.manufacturer ? <p className="text-muted-foreground pointer-events-none text-xs">{item_0.manufacturer}</p> : null}{orderedByLabel ? <p className="text-muted-foreground text-xs"><span>{t("certifiedMedicines.columns.orderedBy")}: </span>{orderedByOrg?.slug ? <Link href={`/app/organizations/${orderedByOrg.slug}`} prefetch={false} className="text-foreground hover:text-primary relative z-10 font-medium underline-offset-2 hover:underline">{orderedByLabel}</Link> : <span className="text-foreground">{orderedByLabel}</span>}</p> : null}<div className="pointer-events-none flex flex-wrap items-center gap-1.5 pt-0.5">{item_0.registrationNumber ? <Badge variant="outline" className="h-5 rounded font-mono text-[10px] tracking-tight">{item_0.registrationNumber}</Badge> : null}{issued ? <span className="text-muted-foreground text-[11px]">{t("certifiedMedicines.columns.certificateIssuedAt")}:{" "}<FormattedDate value={item_0.certificateIssuedAt} locale={intlLocale} variant="dayMonthYear" /></span> : null}{shelfLife ? <span className="text-muted-foreground inline-flex items-center gap-1 text-[11px]"><span aria-label={statusLabel} title={statusLabel} className={cn("inline-block size-2 shrink-0 rounded-full", SHELF_LIFE_STATUS_DOT[status])} />{t("certifiedMedicines.columns.shelfLifeDate")}:{" "}{item_0.shelfLifeDate ? <FormattedDate value={item_0.shelfLifeDate} locale={intlLocale} variant="dayMonthYear" /> : item_0.shelfLifeRaw}</span> : null}</div></div></li>;
    })}</ul>;
  let t16;
  if ($[46] !== t14 || $[47] !== t15) {
    t16 = <div className={t7}>{t14}{t15}</div>;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  return t16;
}
function OrganizationCounterButton(t0) {
  const $ = _c(16);
  const {
    active,
    label,
    count,
    onClick
  } = t0;
  const t1 = `${label} ${count}`;
  const t2 = active && "border-primary/40 bg-primary/10 text-primary hover:bg-primary/15";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("border-border/70 bg-background hover:bg-muted inline-flex h-8 max-w-56 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs transition-colors", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== label) {
    t4 = <span className="truncate">{label}</span>;
    $[2] = label;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = active && "bg-primary/15 text-primary";
  let t6;
  if ($[4] !== t5) {
    t6 = cn("bg-muted text-muted-foreground inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-medium", t5);
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== count || $[7] !== t6) {
    t7 = <span className={t6}>{count}</span>;
    $[6] = count;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== active || $[10] !== onClick || $[11] !== t1 || $[12] !== t3 || $[13] !== t4 || $[14] !== t7) {
    t8 = <button type="button" aria-label={t1} aria-pressed={active} onClick={onClick} className={t3}>{t4}{t7}</button>;
    $[9] = active;
    $[10] = onClick;
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}